import { Horizontal, MaterialIcon, MaterialSymbolIcon, Texto } from '@gravitate-js/excalibrr'

interface RateCellRendererProps {
  value: number
  data?: {
    uom?: string
  }
}

/**
 * Renders a rate with appropriate formatting based on UOM
 */
export function RateCellRenderer({ value, data }: RateCellRendererProps): string | null {
  if (data?.uom === 'Percentage' || data?.uom?.includes?.('%')) {
    return `${(value * 100).toFixed(3)}%`
  }
  // Use simple formatting to avoid thousands separators for rates
  return `$${value?.toFixed(5)}`
}

interface OriginalDisplayProps {
  value: number | string
  currency?: boolean
  bold?: boolean
}

/**
 * Displays the original value with appropriate formatting
 */
export function OriginalDisplay({ value, currency = true }: OriginalDisplayProps): JSX.Element {
  return (
    <Texto align='right' category='p1' weight={currency ? 'bold' : 'regular'}>
      {value?.toLocaleString('en-US', {
        style: currency ? 'currency' : undefined,
        currency: currency ? 'USD' : undefined,
        minimumFractionDigits: currency ? 2 : 2,
        maximumFractionDigits: currency ? 2 : 2,
      })}
    </Texto>
  )
}

interface OverriddenValueDisplayProps {
  value: number | string | null
  align?: 'right' | 'left' | 'center'
}

/**
 * Displays an overridden value with strikethrough styling
 */
export function OverriddenValueDisplay({ value, align }: OverriddenValueDisplayProps): JSX.Element {
  return (
    <Texto
      align={align}
      category='label'
      appearance='hint'
      style={{
        textDecoration: 'line-through',
        lineHeight: 1,
      }}
    >
      {value}
    </Texto>
  )
}

interface OverrideDisplayProps {
  value: number | string
  materialIconProperties?: {name: MaterialSymbolIcon, color?: string} 
  changeStatus?: string
  align?: 'right' | 'left' | 'center'
}

/**
 * Displays an override value with appropriate status icon
 */
export function OverrideDisplay({ value, materialIconProperties, changeStatus, align }: OverrideDisplayProps): JSX.Element {
  return (
    <Horizontal
      verticalCenter
      gap={4}
      flex={1}
      style={{
        overflow: 'visible',
        color: materialIconProperties?.color || 'var(--theme-warning)',
      }}
      justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
    >
      {value && materialIconProperties?.name && (
        <MaterialIcon icon={materialIconProperties?.name} size={changeStatus === 'overridden' ? 12 : 16} style={{ color: materialIconProperties?.color }} />
      )}
      <Texto weight={changeStatus ? 'bold' : 'regular'}>{value}</Texto>
    </Horizontal>
  )
}
